// See https://dev.bandwidth.com/docs/messaging/webhooks/ for detailed info on each field

import z from "zod/v4";
import { dateOrStringAsDate } from "../common/zod-util";

export enum BandwidthWebhookType {
  // Inbound (texts we receive)
  INBOUND_MESSAGE_RECEIVED = "message-received",
  // Outbound (texts we sent)
  OUTBOUND_MESSAGE_SENDING = "message-sending",
  OUTBOUND_MESSAGE_DELIVERED = "message-delivered",
  OUTBOUND_MESSAGE_FAILED = "message-failed",
  OUTBOUND_MESSAGE_READ = "message-read",
}

const bandwidthMessageReceivedWebhookSchema = z.object({
  type: z.literal(BandwidthWebhookType.INBOUND_MESSAGE_RECEIVED),
  time: dateOrStringAsDate,
  to: z.string(),
  description: z.string().nullish(),
  message: z.object({
    id: z.string(),
    owner: z.string(),
    applicationId: z.string(),
    time: dateOrStringAsDate,
    segmentCount: z.number().nullish(),
    direction: z.string(),
    to: z.array(z.string()),
    from: z.string(),
    text: z.string().nullish(),
    tag: z.string().nullish(),
    media: z.array(z.string()).nullish(),
  }),
});
export type BandwidthMessageReceivedWebhook = z.infer<
  typeof bandwidthMessageReceivedWebhookSchema
>;

const baseBandwidthMessageWebhookSchema = z.object({
  time: dateOrStringAsDate,
  description: z.string().nullish(),
  to: z.string(),
  message: z.object({
    id: z.string(),
    owner: z.string(),
    time: dateOrStringAsDate,
    direction: z.string(),
    to: z.array(z.string()),
    from: z.string(),
    text: z.string().nullish(), // will be empty, according to docs
    applicationId: z.string(),
    media: z.array(z.string()).nullish(),
    tag: z.string().nullish(),
    segmentCount: z.number().nullish(),
  }),
});
export type BaseBandwidthMessageWebhook = z.infer<
  typeof baseBandwidthMessageWebhookSchema
>;

const bandwidthMessageSentWebhookSchema =
  baseBandwidthMessageWebhookSchema.extend({
    type: z.literal(BandwidthWebhookType.OUTBOUND_MESSAGE_SENDING),
  });
export type BandwidthMessageSentWebhook = z.infer<
  typeof bandwidthMessageSentWebhookSchema
>;

const bandwidthMessageDeliveredWebhookSchema =
  baseBandwidthMessageWebhookSchema.extend({
    type: z.literal(BandwidthWebhookType.OUTBOUND_MESSAGE_DELIVERED),
  });
export type BandwidthMessageDeliveredWebhook = z.infer<
  typeof bandwidthMessageDeliveredWebhookSchema
>;

const bandwidthMessageFailedWebhookSchema =
  baseBandwidthMessageWebhookSchema.extend({
    type: z.literal(BandwidthWebhookType.OUTBOUND_MESSAGE_FAILED),
    errorCode: z.number().nullish(),
    // description field becomes the error message
  });
export type BandwidthMessageFailedWebhook = z.infer<
  typeof bandwidthMessageFailedWebhookSchema
>;

/** Only applies to RCS */
const bandwidthMessageReadWebhookSchema =
  baseBandwidthMessageWebhookSchema.extend({
    type: z.literal(BandwidthWebhookType.OUTBOUND_MESSAGE_READ),
    // why eventTime instead of time is beyond me - both fields exist for this event type
    eventTime: dateOrStringAsDate,
  });
export type BandwidthMessageReadWebhook = z.infer<
  typeof bandwidthMessageReadWebhookSchema
>;

export const bandwidthWebhookSchema = z.discriminatedUnion("type", [
  bandwidthMessageReceivedWebhookSchema,
  bandwidthMessageSentWebhookSchema,
  bandwidthMessageDeliveredWebhookSchema,
  bandwidthMessageFailedWebhookSchema,
  bandwidthMessageReadWebhookSchema,
]);
export type BandwidthWebhook = z.infer<typeof bandwidthWebhookSchema>;

// Bandwidth sends webhooks as an array, even when there's only one event
// See https://dev.bandwidth.com/docs/messaging/webhooks/
export const bandwidthWebhookArraySchema = z.array(bandwidthWebhookSchema);
export type BandwidthWebhookArray = z.infer<typeof bandwidthWebhookArraySchema>;

// Port-in webhook schemas (XML-based, from Numbers API)
// See https://dev.bandwidth.com/docs/numbers/webhooks/orderWebhook/
export enum BandwidthPortInStatus {
  PENDING_DOCUMENTS = "PENDING_DOCUMENTS",
  SUBMITTED = "SUBMITTED",
  FOC = "FOC",
  REQUESTED_SUPP = "REQUESTED_SUPP",
  COMPLETE = "COMPLETE",
  CANCELLED = "CANCELLED",
  EXCEPTION = "EXCEPTION",
  REQUESTED_CANCEL = "REQUESTED_CANCEL",
}

export const bandwidthPortInWebhookSchema = z.object({
  Notification: z.object({
    SubscriptionId: z.string().nullish(),
    OrderType: z.string(),
    OrderId: z.string(),
    CustomerOrderId: z.string().nullish(),
    Status: z.enum(BandwidthPortInStatus),
    Message: z.string().nullish(),
    Note: z.string().nullish(),
    CompletedTelephoneNumbers: z
      .object({ TelephoneNumber: z.union([z.string(), z.array(z.string())]) })
      .nullish(),
    LastModifiedDate: z.string().nullish(),
    ActualFocDate: z.string().nullish(),
  }),
});

export type BandwidthPortInWebhook = z.infer<
  typeof bandwidthPortInWebhookSchema
>;
